// invoiceDocument — turns an Invoice row into a self-contained, print-ready
// document the user can save as PDF (browser "Save as PDF" from the print
// dialog) or, if pop-ups are blocked, download as a standalone .html file.
//
// Deliberately dependency-free: no PDF library. The generated HTML carries its
// own inline styles (a new window shares no CSS with the app) and auto-opens the
// print dialog. Works for SUCCESS, FAILED and REFUNDED rows — a failed/expired
// payment prints as a payment record noting nothing was charged.

import type { Invoice } from './types';
import { formatDateTime, formatDate, formatMoney } from './dateFormat';

const MERCHANT = 'Breww';

const STATUS_LABEL: Record<Invoice['status'], string> = {
  SUCCESS: 'Paid',
  FAILED: 'Failed',
  REFUNDED: 'Refunded',
};

/** Escape user/gateway-supplied text before it goes into the invoice markup. */
function esc(v: string): string {
  return v.replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string
  ));
}

/** A short, human invoice number derived from the immutable row id. */
function invoiceNumber(invoice: Invoice): string {
  return `INV-${invoice.id.replace(/-/g, '').slice(0, 10).toUpperCase()}`;
}

/** Build the full standalone HTML document for one invoice. */
export function buildInvoiceHtml(invoice: Invoice): string {
  const status = invoice.status;
  const statusLabel = STATUS_LABEL[status] ?? status;
  const isPaid = status === 'SUCCESS';
  const isRefund = status === 'REFUNDED';
  const number = invoiceNumber(invoice);
  const amount = formatMoney(invoice.amount, invoice.currency);

  // Status pill palette.
  const pill = isPaid
    ? { fg: '#0f7a3d', bg: '#e6f6ec' }
    : isRefund
      ? { fg: '#b45309', bg: '#fdf1dc' }
      : { fg: '#c1362f', bg: '#fdecea' };

  const period = invoice.billingPeriodStart || invoice.billingPeriodEnd
    ? `${formatDate(invoice.billingPeriodStart)} – ${formatDate(invoice.billingPeriodEnd)}`
    : null;

  const lineDesc = isRefund ? 'Subscription refund' : 'Subscription payment';
  const lineSub = period ? `Billing period · ${period}` : `Order · ${invoice.paymentServiceOrderId || '—'}`;

  // Payment-details grid (small, muted). Reason for non-paid goes in the notice.
  const details: Array<[string, string]> = [
    ['Order ID', invoice.paymentServiceOrderId || '—'],
    ['Transaction ID', invoice.paymentTransactionId || '—'],
  ];
  if (invoice.gatewayTransactionId) details.push(['Payment reference', invoice.gatewayTransactionId]);
  if (invoice.gateway) details.push(['Gateway', invoice.gateway]);
  const detailsHtml = details
    .map(([k, v]) => `<div class="kv"><div class="k">${esc(k)}</div><div class="v">${esc(v)}</div></div>`)
    .join('');

  const notice = isPaid
    ? { cls: 'ok', text: 'Payment received in full. Thank you.' }
    : isRefund
      ? { cls: 'warn', text: 'This payment was refunded.' }
      : { cls: 'bad', text: `This payment did not complete — no amount was charged.${invoice.failureReason ? ` Reason: ${invoice.failureReason}` : ''}` };

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>${esc(number)} — ${esc(MERCHANT)}</title>
<style>
  /* Kill the browser's own print header/footer (URL + timestamp). */
  @page { margin: 0; }
  :root { --accent:#473C38; --accent-soft:#8a7d75; --cream:#F5EEE6; --ink:#1c1917; --muted:#8a817b; --line:#ece5dd; }
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  html, body { margin: 0; background: #fff; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; color: var(--ink); }
  .bar { height: 6px; background: var(--accent); }
  .sheet { max-width: 720px; margin: 0 auto; padding: 52px 56px 40px; }

  .head { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 40px; }
  .brand { font-size: 25px; font-weight: 800; color: var(--accent); letter-spacing: 0.3px; line-height: 1; }
  .brand small { display: block; margin-top: 7px; font-size: 11px; font-weight: 400; color: var(--muted); letter-spacing: 0.2px; }
  .inv { text-align: right; }
  .inv .t { font-size: 12px; font-weight: 700; letter-spacing: 3px; color: var(--accent-soft); }
  .inv .n { font-size: 16px; font-weight: 700; margin-top: 7px; }

  .meta { display: flex; gap: 48px; padding: 20px 0 28px; border-bottom: 1px solid var(--line); margin-bottom: 4px; }
  .meta .l { font-size: 10.5px; text-transform: uppercase; letter-spacing: 1px; color: var(--muted); margin-bottom: 6px; }
  .meta .v { font-size: 14px; font-weight: 600; }
  .badge { display: inline-block; padding: 5px 13px; border-radius: 999px; font-size: 12px; font-weight: 700; }

  table.items { width: 100%; border-collapse: collapse; margin-top: 8px; }
  table.items th { text-align: left; font-size: 10.5px; letter-spacing: 1px; text-transform: uppercase; color: var(--muted); padding: 14px 0 12px; border-bottom: 1px solid var(--line); }
  table.items th.r, table.items td.r { text-align: right; }
  table.items td { padding: 18px 0; border-bottom: 1px solid var(--line); font-size: 14px; vertical-align: top; }
  table.items .desc { font-weight: 600; }
  table.items .sub { font-size: 12px; color: var(--muted); margin-top: 4px; }
  table.items td.r { font-weight: 600; white-space: nowrap; }

  .totals { display: flex; justify-content: flex-end; margin-top: 20px; }
  .totals .box { width: 280px; }
  .totals .row { display: flex; justify-content: space-between; padding: 7px 0; font-size: 14px; color: var(--muted); }
  .totals .row .v { color: var(--ink); font-weight: 500; }
  .totals .grand { margin-top: 8px; padding-top: 15px; border-top: 2px solid var(--accent); font-size: 19px; font-weight: 800; color: var(--ink); }
  .totals .grand .v { font-weight: 800; }

  .notice { margin-top: 34px; padding: 14px 18px; border-radius: 10px; font-size: 13px; line-height: 1.5; }
  .notice.ok { background: #eaf7ef; color: #0f7a3d; }
  .notice.warn { background: #fdf3e2; color: #b45309; }
  .notice.bad { background: #fdeeec; color: #b42318; }

  .details { margin-top: 30px; background: var(--cream); border-radius: 14px; padding: 22px 26px; }
  .details h3 { margin: 0 0 16px; font-size: 11px; letter-spacing: 1px; text-transform: uppercase; color: var(--accent); }
  .details .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px 40px; }
  .details .k { font-size: 10.5px; color: var(--muted); text-transform: uppercase; letter-spacing: 0.5px; }
  .details .v { font-size: 13px; margin-top: 3px; word-break: break-all; font-variant-numeric: tabular-nums; }

  .foot { margin-top: 46px; padding-top: 18px; border-top: 1px solid var(--line); text-align: center; color: #b3a99f; font-size: 11px; }
</style>
</head>
<body onload="setTimeout(function(){ window.print(); }, 200)">
  <div class="bar"></div>
  <div class="sheet">
    <div class="head">
      <div class="brand">${esc(MERCHANT)}<small>Secured by DataCaffe Payments</small></div>
      <div class="inv"><div class="t">INVOICE</div><div class="n">${esc(number)}</div></div>
    </div>

    <div class="meta">
      <div><div class="l">Date issued</div><div class="v">${esc(formatDate(invoice.createdAt))}</div></div>
      ${period ? `<div><div class="l">Billing period</div><div class="v">${esc(period)}</div></div>` : ''}
      <div><div class="l">Status</div><div class="v"><span class="badge" style="color:${pill.fg};background:${pill.bg}">${esc(statusLabel)}</span></div></div>
    </div>

    <table class="items">
      <thead><tr><th>Description</th><th class="r">Amount</th></tr></thead>
      <tbody><tr>
        <td><div class="desc">${esc(lineDesc)}</div><div class="sub">${esc(lineSub)}</div></td>
        <td class="r">${esc(amount)}</td>
      </tr></tbody>
    </table>

    <div class="totals"><div class="box">
      <div class="row"><span>Subtotal</span><span class="v">${esc(amount)}</span></div>
      <div class="row grand"><span>Total ${isRefund ? 'refunded' : isPaid ? 'paid' : 'due'}</span><span class="v">${esc(amount)}</span></div>
    </div></div>

    <div class="notice ${notice.cls}">${esc(notice.text)}</div>

    <div class="details">
      <h3>Payment details</h3>
      <div class="grid">${detailsHtml}</div>
    </div>

    <div class="foot">Generated ${esc(formatDateTime(new Date().toISOString()))} · ${esc(MERCHANT)}</div>
  </div>
</body>
</html>`;
}

/**
 * Open the invoice in a print-ready window (which auto-opens the print dialog so
 * the user can "Save as PDF"). If the pop-up is blocked, fall back to
 * downloading the same document as a standalone .html file.
 */
export function downloadInvoice(invoice: Invoice): void {
  const html = buildInvoiceHtml(invoice);
  const win = window.open('', '_blank', 'width=820,height=920');
  if (win) {
    win.document.open();
    win.document.write(html);
    win.document.close();
    return;
  }
  // Pop-up blocked — hand the user a file instead.
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${invoiceNumber(invoice)}.html`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
